
import axios from 'axios'
import { defineComponent, inject, ref } from 'vue'
import { useRouter } from 'vue-router'
import { AuthPlugin } from '@/auth'
import EchoInspectionUpload from '@/components/EchoInspectionUpload.vue'

export default defineComponent({
  name: 'Prediction',
  components: { EchoInspectionUpload },
  setup() {
    const router = useRouter()
    const inspection = ref<typeof EchoInspectionUpload>()
    const isRunning = ref(false)
    const predictedBms = ref()
    const predictedRank5Percentage = ref()
    const predictionUri = ref()
    const predictionId = ref()
    const searchTrapeziusThickness = ref()
    const searchTrapeziusSubcutaneousFatThickness = ref()
    const searchSubcutaneousFatThickness = ref()
    const searchLatissimusThickness = ref()

    const backendUrl: string | undefined = inject('BackendUrl')
    if (!backendUrl) {
      throw new Error('BackendUrl is not provided')
    }

    const auth: AuthPlugin | undefined = inject('Auth')
    if (!auth) {
      throw new Error('Auth plugin is not provided')
    }

    const predict = async () => {
      try {
        isRunning.value = true

        const data = new FormData()
        inspection.value?.uploadFile && data.append('inspection', inspection.value.uploadFile)

        const idToken = (await auth.getIdTokenClaims({}))?.__raw
        const impersonationToken = localStorage.getItem('impersonationToken')

        const headers: Record<string, string> = {
          'content-type': 'multipart/form-data',
          Authorization: `Bearer ${idToken}`,
        }

        if (impersonationToken) {
          headers['X-AIMEAT-Impersonation-Token'] = impersonationToken
        }

        const response = await axios.post(`${backendUrl}/v1/prediction`, data, {
          headers,
        })

        predictionId.value = response.data.id
        predictedBms.value = response.data.BMS
        // 5等級以上割合の計算（BMS 8以上の確率）
        const bms8BelowProbability = Math.round(response.data.CORN.probabilities[4] * 10) * 10
        predictedRank5Percentage.value = 100 - bms8BelowProbability
        predictedRank5Percentage.value = Math.max(10, Math.min(90, predictedRank5Percentage.value))
        predictionUri.value = response.headers['x-aimeat-predictionuri']
      } catch (error) {
        predictionId.value = null
        predictedBms.value = null
        predictedRank5Percentage.value = null
        predictionUri.value = null
      }
      finally {
        isRunning.value = false
      }
    }

    const search = async () => {
      if (
        searchTrapeziusThickness.value != null &&
        searchTrapeziusSubcutaneousFatThickness.value != null &&
        searchSubcutaneousFatThickness.value != null &&
        searchLatissimusThickness.value != null
      ) {
        const idToken = (await auth.getIdTokenClaims({}))?.__raw
        const impersonationToken = localStorage.getItem('impersonationToken')
        const bms = predictedBms.value
        const tt = parseFloat(searchTrapeziusThickness.value)
        const tsft = parseFloat(searchTrapeziusSubcutaneousFatThickness.value)
        const sft = parseFloat(searchSubcutaneousFatThickness.value)
        const lt = parseFloat(searchLatissimusThickness.value)

        const exampleHeaders: Record<string, string> = {
          Authorization: `Bearer ${idToken}`,
          'X-AIMEAT-PredictionUri': predictionUri.value,
        }

        if (impersonationToken) {
          exampleHeaders['X-AIMEAT-Impersonation-Token'] = impersonationToken
        }

        // 類似画像検索は必要ないが、検索データの記録のために実行する
        await axios.get(`${backendUrl}/v1/example`, {
          headers: exampleHeaders,
          params: { bms, tt, tsft, sft, lt },
        })
      }

      router.push(`/${predictionId.value}`)
    }

    return {
      inspection,
      isRunning,
      predictedBms,
      predictedRank5Proba: predictedRank5Percentage,
      searchTrapeziusThickness,
      searchTrapeziusSubcutaneousFatThickness,
      searchSubcutaneousFatThickness,
      searchLatissimusThickness,
      predict,
      search,
    }
  },
})
